import { Box, Button, Grid, Typography } from '@mui/material';
import api from 'api';
import AddIcon from '@mui/icons-material/Add';
import BodyTemplate from 'components/_layout/navigation/BodyTemplate';
import { GenericPaginator } from 'components/generic-table';
import FindPatientsForm from 'components/find-patients-form';
import PatientList from 'components/patient-list';
import { useAuthenticatedUser, useContextRedirection, useNotification } from 'hooks';
import { Patient, PatientsListRequest, PatientsListResponse } from 'models/patients.model';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GlobalState } from 'store/reducers';
import { DEFAULT_PAGINATION_SIZE } from 'utils/Constants';
import { CREATE_BY_CNS_LENGTH } from '../patients';
import RoutePaths from 'utils/RoutePaths';
import { formatCreatedPatient } from 'utils/func/Person.func';
import { allPropsAreTruthy } from 'utils';
import { PrescriptionRequestMaker } from 'utils/func/Prescription.func';

const formData: PatientsListRequest = {
  first_name: undefined,
  last_name: undefined,
  cns: undefined,
  gender: undefined,
  birth_name: undefined,
  birth_date: undefined,
};

const paginatorData: GenericPaginator = {
  total: 0,
  size: DEFAULT_PAGINATION_SIZE,
  page: 1,
};

const Prescriptions: FC = () => {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const navigate = useContextRedirection();
  const authenticatedUser = useAuthenticatedUser();

  const currentUser = useSelector((state: GlobalState) => state.session.authenticateUser);

  const [form, setForm] = useState(formData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreatePatientButton, setShowCreatePatientButton] = useState<boolean>(false);

  const handleFormData = (data: PatientsListRequest) => {
    setForm(data);
    const resetedPaginator = { ...paginator, page: 1 };
    setPaginator(resetedPaginator);
    requestPatientList(resetedPaginator, data);
  };

  const [isDataFromMPI, setIsDataFromMPI] = useState<boolean>(false);
  const [paginator, setPaginator] = useState(paginatorData);
  const patientListData: Patient[] | undefined = undefined;
  const [patientList, setPatientList] = useState<Patient[] | undefined>(patientListData);

  const handlePaginatorData = (data: GenericPaginator) => {
    setPaginator(data);
    requestPatientList(data, form);
  };

  const requestPatientListMPI = useCallback(
    (data: PatientsListRequest) => {
      setIsLoading(true);

      api.patients
        .getPatientsMPI(data)
        .then((response: any) => {
          setIsDataFromMPI(true);
          setPatientList(response);
          setPaginator({ ...paginator, total: response.length });
          setIsLoading(false);
        })
        .catch((error) => {
          if (error?.code === 400) {
            notification(t('landing_page.login.token.notification.error.incorrectToken'), 'error');
            return;
          }
          setPatientList([]);
          setPaginator(paginatorData);
          setIsLoading(false);
        });
    },
    [notification, paginator, t],
  );
  const requestPatientList = useCallback(
    (paginator: GenericPaginator, form: PatientsListRequest) => {
      setIsLoading(true);
      api.patients
        .getPatients(form, paginator.size, paginator.page)
        .then((response: PatientsListResponse) => {
          if (response.total === 0) {
            requestPatientListMPI(form);
          } else {
            setIsDataFromMPI(false);
            setPatientList(response.results);
            setPaginator({
              total: response.total,
              size: response.size,
              page: response.page,
            });
            setIsLoading(false);
          }
        })
        .catch(() => {
          setPatientList([]);
          notification(t('prescriptions.notification.error'), 'error');
          setIsLoading(false);
        })
        .finally(() => setShowCreatePatientButton(true));
    },
    [currentUser?.patient_creation_enabled, notification, requestPatientListMPI, t],
  );
  const requestCreatePatient = useCallback(
    (patient: any) => {
      const { birth_name, ...formatedPatient } = formatCreatedPatient(patient);
      if (allPropsAreTruthy(formatedPatient)) {
        api.patients
          .createPatient({ ...formatedPatient, home: patient.addresses?.home })
          .then((response) => {
            PrescriptionRequestMaker({ patientId: response.id, navigation: navigate });
          })
          .catch((e) => {
            const errorMessages = ['There is more than one patient with that social security number.', 'There is more than one patient with those data.'];
            if (errorMessages.includes(e.response?.data?.message)) {
              notification(`${t('patients.create.errors.already_exist')}`, 'error');
            }
          });
      }
    },
    [navigate, notification, t],
  );

  return (
    <BodyTemplate title={t('bodyTemplate.doctor.prescriptions')}>
      <Grid container columnSpacing={2} rowSpacing={3}>
        <Grid item xs={12} md={4}>
          <FindPatientsForm handleFormAction={handleFormData} />
        </Grid>
        <Grid item xs={12} md={8}>
          <PatientList
            patientDetailAction={{
              to: 'DOCTOR_PATIENTS_DETAILS',
            }}
            showPrescriptionAction
            patientList={patientList}
            paginatorData={paginator}
            showPoctPrescriptionAction={authenticatedUser?.show_poct}
            handlePaginatorAction={handlePaginatorData}
            isDataFromMPI={isDataFromMPI}
            isLoading={isLoading}
            requestCreatePatient={requestCreatePatient}
          />
          {showCreatePatientButton && !!currentUser?.patient_creation_enabled && form.cns?.length === CREATE_BY_CNS_LENGTH && (
            <Box display='flex' alignItems='center' justifyContent='flex-end' mt={3}>
              <Typography variant='h2' fontSize='14px' mr={2}>
                {t('patients.create.description')}
              </Typography>
              <Button variant='contained' startIcon={<AddIcon />} onClick={() => navigate(RoutePaths.DOCTOR_CREATE_PATIENTS)}>
                {t('patients.create.button_title')}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </BodyTemplate>
  );
};

export default Prescriptions;
